import { Controller } from "@hotwired/stimulus"

const MIN_ZOOM = 100
const MAX_ZOOM = 250
const ZOOM_STEP = 25

// Layout offset from the top of the document, ignoring CSS transforms.
// The modal dialog scales in while it opens, so bounding rects taken during
// that window are shrunk; offset* metrics describe the settled layout.
function layoutTop(element) {
  let top = 0
  for (let node = element; node; node = node.offsetParent) top += node.offsetTop
  return top
}

export default class extends Controller {
  static targets = ["scroller", "canvas", "zoomIn", "zoomOut", "zoomLevel"]
  static values = {
    zoom: { type: Number, default: MIN_ZOOM }
  }

  connect() {
    this.resizeObserver = new ResizeObserver(() => this.fitHeight())
    if (this.hasScrollerTarget) this.resizeObserver.observe(this.scrollerTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
  }

  center() {
    if (!this.hasScrollerTarget) return

    requestAnimationFrame(() => {
      this.scrollerTarget.scrollLeft = (this.scrollerTarget.scrollWidth - this.scrollerTarget.clientWidth) / 2
    })
  }

  zoomIn() {
    this.zoomValue = Math.min(this.zoomValue + ZOOM_STEP, MAX_ZOOM)
    this.center()
  }

  zoomOut() {
    this.zoomValue = Math.max(this.zoomValue - ZOOM_STEP, MIN_ZOOM)
    this.center()
  }

  canvasTargetConnected() {
    this.fitHeight()
    this.renderZoom()
  }

  scrollerTargetConnected(element) {
    this.resizeObserver?.observe(element)
  }

  scrollerTargetDisconnected(element) {
    this.resizeObserver?.unobserve(element)
  }

  fitHeight() {
    if (!this.hasCanvasTarget || !this.hasScrollerTarget) return

    this.scrollerTarget.style.overflowY = this.zoomValue === MIN_ZOOM ? "hidden" : "auto"
    const aspectRatio = Number(this.canvasTarget.dataset.aspectRatio)
    const styles = getComputedStyle(this.scrollerTarget)
    const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
    const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
    const availableWidth = this.scrollerTarget.offsetWidth - horizontalPadding
    if (availableWidth <= 0) return

    const availableHeight = this.availableModalMapHeight(verticalPadding)
    const fittedWidth = availableHeight
      ? Math.min(availableWidth, availableHeight * aspectRatio)
      : availableWidth
    const fittedHeight = fittedWidth / aspectRatio

    this.fitScale = (fittedWidth / availableWidth) * 100
    this.scrollerTarget.style.height = `${fittedHeight + verticalPadding}px`
    this.renderZoom()
  }

  zoomValueChanged() {
    this.renderZoom()
  }

  renderZoom() {
    if (!this.hasCanvasTarget) return

    const fittedZoom = (this.fitScale || 100) * (this.zoomValue / 100)
    this.canvasTarget.style.width = `${fittedZoom}%`
    this.scrollerTarget.style.overflowY = this.zoomValue === MIN_ZOOM ? "hidden" : "auto"
    if (this.hasZoomLevelTarget) this.zoomLevelTarget.textContent = `${this.zoomValue}%`
    if (this.hasZoomOutTarget) this.zoomOutTarget.disabled = this.zoomValue === MIN_ZOOM
    if (this.hasZoomInTarget) this.zoomInTarget.disabled = this.zoomValue === MAX_ZOOM
  }

  availableModalMapHeight(verticalPadding) {
    const modalBody = this.scrollerTarget.closest("[data-seat-map-modal-body]")
    if (!modalBody) return null

    const bodyStyles = getComputedStyle(modalBody)
    const bodyPaddingTop = parseFloat(bodyStyles.paddingTop)
    const bodyPaddingBottom = parseFloat(bodyStyles.paddingBottom)
    const heightBeforeMap = layoutTop(this.scrollerTarget) - layoutTop(modalBody) - bodyPaddingTop

    return Math.max(
      modalBody.clientHeight - bodyPaddingTop - bodyPaddingBottom - heightBeforeMap - verticalPadding,
      0,
    )
  }
}
